// Task group view page - Card-based read-only display

import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo, useCallback } from 'react';
import {
  VStack,
  HStack,
  Box,
  Text,
  Badge,
  Card,
  CardBody,
  Icon,
  Button,
  Link,
  Progress,
  useDisclosure,
} from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { MdOpenInNew, MdLocationOn } from 'react-icons/md';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { LoadingSpinner } from '@/client/components/shared/LoadingSpinner';
import { EmptyState } from '@/client/components/shared/EmptyState';
import { TaskCard, TaskCardStatus } from '../components/TaskCard';
import { Modal } from '@/design-system/components/Modal';
import { MediaDisplay } from '@/client/components/data/MediaDisplay';
import { useDataGroup } from '@/lib/sdk/chatbot/queries';
import { formatters } from '@/client/utils/formatters';
import { isHiddenTaskType } from '@/client/utils/taskTypeMapping';
import { hasMediaAttachments, getMediaCount } from '@/client/utils/taskDataAdapter';
import type { TaskData } from '@/lib/sdk/chatbot/types';

export function TaskGroupViewPage() {
  const { botId, orgId, groupId } = useParams<{
    botId: string;
    orgId: string;
    groupId: string;
  }>();
  const navigate = useNavigate();
  const { data: taskGroup, isLoading } = useDataGroup(botId!, groupId!);

  const [selectedTask, setSelectedTask] = useState<TaskData | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const tasks = useMemo(() => {
    if (!taskGroup?.items) return [];
    return taskGroup.items.filter((task) => !isHiddenTaskType(task.type));
  }, [taskGroup]);

  const completedTasks = tasks.filter((t) => t.note && t.note.trim() !== '').length;
  const totalTasks = tasks.length;
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  const handleBack = () => {
    navigate(`/dashboard/${orgId}/workflows/${botId}/data`);
  };

  const handleViewTask = useCallback(
    (task: TaskData) => {
      setSelectedTask(task);
      onOpen();
    },
    [onOpen]
  );

  const handleCloseModal = useCallback(() => {
    setSelectedTask(null);
    onClose();
  }, [onClose]);

  const getDisplayValue = (task: TaskData): string => {
    const parts: string[] = [];
    if (task.note) parts.push(task.note);
    if (hasMediaAttachments(task)) {
      const count = getMediaCount(task);
      parts.push(`📎 ${count} attachment${count !== 1 ? 's' : ''}`);
    }
    return parts.join(' • ') || '';
  };

  const getTaskStatus = (task: TaskData): TaskCardStatus => {
    if (task.note && task.note.trim() !== '') return 'completed';
    return 'pending';
  };

  const breadcrumbs = [
    { label: 'Workflows', href: `/dashboard/${orgId}/workflows` },
    { label: 'Data', href: `/dashboard/${orgId}/workflows/${botId}/data` },
    { label: 'View Data Group' },
  ];

  if (isLoading) {
    return <LoadingSpinner message="Loading task group..." />;
  }

  if (!taskGroup) {
    return (
      <PageContainer title="Task Group Not Found" breadcrumbs={breadcrumbs}>
        <EmptyState
          icon={<Icon as={FiArrowLeft} boxSize={12} />}
          title="Task group not found"
          description="The requested task group could not be found."
          action={{ label: 'Back to Tasks', onClick: handleBack }}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer title={`View Data Group - ${taskGroup.user_name}`} breadcrumbs={breadcrumbs}>
      <VStack spacing={6} align="stretch" maxW="4xl" mx="auto">
        <Card>
          <CardBody>
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="text-secondary"><strong>User:</strong> {taskGroup.user_name}</Text>
                  <Text fontSize="sm" color="text-secondary"><strong>Date:</strong> {formatters.date(taskGroup.date)}</Text>
                </VStack>
                <Badge colorScheme="purple" fontSize="sm">{taskGroup.source_key}</Badge>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="medium" color="text-primary">Progress</Text>
                <Badge colorScheme="brand" fontSize="sm">{completedTasks} / {totalTasks} completed</Badge>
              </HStack>
              <Progress value={progress} colorScheme="brand" size="sm" borderRadius="full" />
            </VStack>
          </CardBody>
        </Card>

        <VStack spacing={3} align="stretch">
          {tasks.map((task, index) => (
            <TaskCard
              key={task.id}
              title={task.name}
              number={index + 1}
              status={getTaskStatus(task)}
              displayValue={getDisplayValue(task)}
              onClick={() => handleViewTask(task)}
              actionLabel="View"
              actionTooltip="View task details"
            />
          ))}
        </VStack>

        <Button variant="outline" leftIcon={<Icon as={FiArrowLeft} />} onClick={handleBack}>
          Back to Data List
        </Button>
      </VStack>

      {selectedTask && (
        <Modal
          isOpen={isOpen}
          onClose={handleCloseModal}
          title={selectedTask.name}
          footer={<Button variant="ghost" onClick={handleCloseModal}>Close</Button>}
          size="md"
        >
          <VStack spacing={4} align="stretch">
            <HStack flexWrap="wrap" gap={2}>
              <Badge colorScheme="purple" fontSize="xs">{selectedTask.type}</Badge>
              {selectedTask.Is_edited && (
                <Badge colorScheme="yellow" fontSize="xs">Edited</Badge>
              )}
            </HStack>
            {selectedTask.description && (
              <Text fontSize="sm" color="text-secondary">{selectedTask.description}</Text>
            )}
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="text-secondary" mb={1}>Response</Text>
              <Text fontSize="md" p={3} bg="bg-muted" borderRadius="md" borderWidth="1px" borderColor="border-default" color="text-primary">
                {selectedTask.note || 'No response'}
              </Text>
            </Box>
            {selectedTask.location?.latitude && selectedTask.location?.longitude && (
              <Box>
                <HStack spacing={1} mb={1}>
                  <Icon as={MdLocationOn} color="text-muted" boxSize={4} />
                  <Text fontSize="sm" fontWeight="medium" color="text-secondary">Location</Text>
                </HStack>
                <Link
                  href={`https://www.google.com/maps?q=${selectedTask.location.latitude},${selectedTask.location.longitude}`}
                  isExternal
                  fontSize="sm"
                  color="brand.500"
                >
                  {selectedTask.location.latitude.toFixed(6)}, {selectedTask.location.longitude.toFixed(6)}
                  <Icon as={MdOpenInNew} ml={1} boxSize={3} />
                </Link>
              </Box>
            )}
            {hasMediaAttachments(selectedTask) && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" color="text-secondary" mb={2}>Attachments</Text>
                <MediaDisplay
                  photos={selectedTask.photos}
                  videos={selectedTask.videos}
                  documents={selectedTask.documents}
                  media={selectedTask.media}
                />
              </Box>
            )}
          </VStack>
        </Modal>
      )}
    </PageContainer>
  );
}
